import clsx from 'clsx';
import { HeartIcon, XIcon } from 'lucide-preact';
import { ComponentProps } from 'preact';

import { Button } from '@/components/Buttons';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import { FollowHabitatTrackingData, useFollowHabitat } from '@/hooks/habitats';
import { useSelector } from '@/redux/helper';

interface FollowButtonProps extends ComponentProps<typeof Button> {
  habitatId: string;
  trackingData?: Omit<FollowHabitatTrackingData, 'habitatId'>;
}

const FollowButton = ({ habitatId, trackingData, onClick: onClickProp, className, ...props }: FollowButtonProps) => {
  const openSpeedyFlow = useOpenSpeedyFlow();
  const isLoggedIn = useSelector((state) => state.user.logged);
  const { isFavorited, follow, unfollow, isError } = useFollowHabitat(habitatId, {
    trackingData: {
      habitatId,
      ...trackingData,
    },
  });

  const onClick: ComponentProps<typeof Button>['onClick'] = (e) => {
    if (!isLoggedIn) {
      // They clicked Follow, so follow once they have an account. Without this they come back from
      // signing up not following the habitat, with nothing to say their click was swallowed.
      openSpeedyFlow({ kind: 'account', entryPoint: 'follow-habitat' }, follow);
      onClickProp?.(e);
      return;
    }

    if (isFavorited) {
      unfollow();
    } else {
      follow();
    }
    onClickProp?.(e);
  };

  return (
    <Button
      variant="tertiary"
      onClick={onClick}
      data-tour-target="follow"
      className={clsx(
        'shadow-xs',
        'group-data-[tour-glow=follow]/tour:animate-glow-ring',
        isFavorited
          ? 'bg-tea-100 text-forest-dark hover:bg-tea-200 focus-visible:bg-tea-200 focus-visible:ring-forest-dark'
          : undefined,
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {isError ? (
        <XIcon className="size-4 text-system-red" />
      ) : (
        <HeartIcon className={isFavorited ? 'fill-current' : undefined} />
      )}
      {isError ? 'Try again' : isFavorited ? 'Following' : 'Follow'}
    </Button>
  );
};

export default FollowButton;
